// api/kb.ts
import { apiJson } from './client';

export interface ArgomentoDto {
  id: string;
  name_it: string;
  name_en?: string;
  sort_order: number;
}

export interface PrincipleDto {
  id: string;
  argomento_id: string;
  principle_number: number;
  name_it: string;
  enunciato: string;
  regola?: string;
  /** Principle-level sign images, relative paths e.g. "img_sign/12.png" or
   *  "img_sign_by_hash/HASH.png". Already returned by `/api/principles/:id`
   *  via `select('*')`; ~41% of principles carry 1–4 signs (spec §5.6). */
  images?: string[] | null;
  argomenti?: { name_it: string };  // joined
}

export interface QuestionDto {
  id: string;
  argomento_id: string;
  text_it: string;
  is_true: boolean;
  image_ref: string | null;   // e.g. "img_sign_by_hash/HASH.png" OR "img_sign/PLAIN.png" OR null
  argomenti?: { name_it: string };  // joined via Supabase `argomenti!inner(name_it)`
}

// --- FP6 Appendice v2 — theory chapter -------------------------------------
// The active chapter client lives in `api/appendice.ts`; subsection images are
// now served there as chapter/subsection-owned theory images.

// NOTE (V28xFP merge): the per-argomento chapter client is consolidated into
// `api/appendice.ts` (richer types — `contenuto: string | string[]`, typed
// `prof_layer`). The duplicate `ChapterDto`/`ChapterSubSectionDto`/
// `ChapterKeyRuleDto` types and `kbApi.getChapter` that used to live here were
// removed; import `AppendiceChapter` from `./appendice` instead. The official
// question browser API below (FP6) stays in kb.ts — no appendice.ts equivalent.

// --- FP6 Appendice v2 — per-argomento question browser ---------------------

export interface ArgomentoQuestionDto {
  id: string;
  text_it: string;
  is_true: boolean;
  image_ref: string | null;
  /** Full principle ids (`argN_PK`) this question tests. */
  principle_ids: string[];
  /** Per-principle proof text — the "perché" the answer is V/F. */
  proofs: Array<{ principle_id: string; proof_text: string }>;
}

export const kbApi = {
  async listArgomenti() {
    const res = await apiJson<{ argomenti: ArgomentoDto[] }>('/api/argomenti');
    return res.argomenti;
  },

  async listPrinciples(argomentoId: string) {
    const res = await apiJson<{ principles: PrincipleDto[] }>(`/api/argomenti/${argomentoId}/principles`);
    return res.principles;
  },

  async getPrinciple(id: string) {
    const res = await apiJson<{ principle: PrincipleDto }>(`/api/principles/${id}`);
    return res.principle;
  },

  async randomQuestions(opts: {
    argomentoId?: string;
    excludeIds?: string[];
    /** W2.2: deduplicate by image. Question IDs alone don't catch the case
     *  where two distinct questions reference the same sign image. */
    excludeImageRefs?: string[];
    count?: number;
  } = {}) {
    const params = new URLSearchParams();
    if (opts.argomentoId) params.set('argomentoId', opts.argomentoId);
    if (opts.excludeIds?.length) params.set('excludeIds', opts.excludeIds.join(','));
    if (opts.excludeImageRefs?.length) params.set('excludeImageRefs', opts.excludeImageRefs.join(','));
    if (opts.count) params.set('count', String(opts.count));
    const res = await apiJson<{ questions: QuestionDto[] }>(`/api/questions/random?${params}`);
    return res.questions;
  },

  async searchPrinciples(q: string, limit = 20) {
    if (q.trim().length < 2) return [];
    const params = new URLSearchParams();
    params.set('q', q.trim());
    params.set('limit', String(limit));
    const res = await apiJson<{ principles: PrincipleDto[] }>(`/api/principles?${params}`);
    return res.principles;
  },

  // FP6 Appendice v2 — per-argomento official-question browser with proofs.
  async listArgomentoQuestions(argomentoId: string) {
    const res = await apiJson<{ questions: ArgomentoQuestionDto[] }>(`/api/argomenti/${argomentoId}/questions`);
    return res.questions;
  },
};
